import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, IconButton, styled } from '@mui/material';
import React, { useEffect, useState } from 'react';

interface ProjectImageCarouselProps {
  images: string[];
  alt: string;
}

const CarouselContainer = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  borderRadius: '12px',
  background: 'rgba(0, 0, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ImageContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
});

const CarouselImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  flexShrink: 0,
});

const CarouselControls = styled(Box)({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  opacity: 0,
  transition: 'opacity 0.3s ease',
});

const CarouselButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  width: '40px',
  height: '40px',
  zIndex: 2,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
  },
  '&.left': {
    left: '10px',
  },
  '&.right': {
    right: '10px',
  },
}));

const DotsContainer = styled(Box)(() => ({
  position: 'absolute',
  bottom: '15px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '8px',
  zIndex: 2,
}));

const Dot = styled(Box)<{ active: boolean }>(({ active }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: active ? 'rgba(0, 173, 181, 0.9)' : 'rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: active ? 'rgba(0, 173, 181, 1)' : 'rgba(255, 255, 255, 0.8)',
  },
}));

const ProjectImageCarousel: React.FC<ProjectImageCarouselProps> = ({ images, alt }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-advance carousel
  useEffect(() => {
    if (images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  if (images.length === 0) {
    return (
      <CarouselContainer>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '1.2rem',
          }}
        >
          No images available
        </Box>
      </CarouselContainer>
    );
  }

  return (
    <CarouselContainer>
      <ImageContainer
        sx={{
          transform: `translateX(-${currentIndex * 100}%)`,
        }}
      >
        {images.map((image, index) => (
          <CarouselImage
            key={index}
            src={image}
            alt={`${alt} - Image ${index + 1}`}
            loading="lazy"
          />
        ))}
      </ImageContainer>

      {images.length > 1 && (
        <>
          {/* Previous Button */}
          <CarouselControls className="carousel-controls left">
            <CarouselButton
              onClick={goToPrevious}
              disabled={images.length <= 1}
              aria-label="Previous image"
            >
              <ChevronLeft />
            </CarouselButton>
          </CarouselControls>

          {/* Next Button */}
          <CarouselControls className="carousel-controls right">
            <CarouselButton
              onClick={goToNext}
              disabled={images.length <= 1}
              aria-label="Next image"
            >
              <ChevronRight />
            </CarouselButton>
          </CarouselControls>

          {/* Dots Indicator */}
          <DotsContainer>
            {images.map((_, index) => (
              <Dot
                key={index}
                active={index === currentIndex}
                onClick={() => goToSlide(index)}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </DotsContainer>
        </>
      )}
    </CarouselContainer>
  );
};

export default ProjectImageCarousel; 